/**
 * Tenant branding bootstrap for the apply portal (Phase H0 hotfix
 * mirror).
 *
 * Identical contract to the IA-side provider: read public branding
 * + write `--color-primary` / `--color-secondary` / favicon link to
 * `document.documentElement`. We deliberately don't surface a logo
 * via context because the AppFrame chrome is intentionally minimal
 * — the page-level components (IntakePage, TrackPage, ApplyPage)
 * render the institution name from their own fetches and don't
 * need a frame-level logo slot.
 */

import * as React from "react";
import { useTenantBranding } from "@/lib/branding/api";

interface TenantBrandingProviderProps {
  slug: string | null | undefined;
  children: React.ReactNode;
}

export function TenantBrandingProvider({ slug, children }: TenantBrandingProviderProps) {
  const { data: branding } = useTenantBranding(slug);

  React.useEffect(() => {
    if (!branding) return;
    const root = document.documentElement;
    const previous = {
      primary: root.style.getPropertyValue("--color-primary"),
      secondary: root.style.getPropertyValue("--color-secondary"),
    };
    if (branding.primary_color) {
      root.style.setProperty("--color-primary", branding.primary_color);
    }
    if (branding.secondary_color) {
      root.style.setProperty("--color-secondary", branding.secondary_color);
    }
    if (branding.favicon_url) {
      const link =
        document.querySelector<HTMLLinkElement>("link[rel='icon']") ??
        Object.assign(document.createElement("link"), { rel: "icon" });
      link.href = branding.favicon_url;
      if (!link.parentNode) document.head.appendChild(link);
    }
    return () => {
      if (previous.primary) {
        root.style.setProperty("--color-primary", previous.primary);
      } else {
        root.style.removeProperty("--color-primary");
      }
      if (previous.secondary) {
        root.style.setProperty("--color-secondary", previous.secondary);
      } else {
        root.style.removeProperty("--color-secondary");
      }
    };
  }, [branding]);

  return <>{children}</>;
}
